import { FC, useCallback, useEffect, useRef, useState } from "react";
import { Box, Chip, Stack, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import HistoryIcon from "@mui/icons-material/History";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import SwipeIcon from "@mui/icons-material/Swipe";
import HighlightAltIcon from "@mui/icons-material/HighlightAlt";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import IconButton from "@mui/material/IconButton";

type CompareMode = "drag" | "hover";

interface InvoicePdfComparisonProps {
  /** PDF shown on the left of the divider (the older version). */
  beforeUrl: string;
  /** PDF shown on the right of the divider (the newer version). */
  afterUrl: string;
  beforeLabel?: string;
  afterLabel?: string;
  /** 'drag' needs a press on the handle, 'hover' follows the pointer across the sheet. */
  defaultMode?: CompareMode;
  /** Starting divider position, 0-100. */
  initialPosition?: number;
  height?: string | number;
}

// Matches the app theme primary so the slider sits with the rest of the invoice UI.
const ACCENT = "#059669";

/**
 * Before/after comparison of two invoice PDFs. Both PDFs are stacked and the newer one is clipped
 * at the divider, so moving the handle wipes between the two versions of the same invoice.
 *
 * PDF panes render in iframes, which swallow pointer events. While dragging (and for the whole
 * time in hover mode) a transparent overlay sits on top so the pointer keeps reaching us.
 */
const InvoicePdfComparison: FC<InvoicePdfComparisonProps> = ({
  beforeUrl,
  afterUrl,
  beforeLabel = "Previous",
  afterLabel = "Regenerated",
  defaultMode = "drag",
  initialPosition = 50,
  height = "70vh",
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [position, setPosition] = useState(Math.min(100, Math.max(0, initialPosition)));
  const [isDragging, setIsDragging] = useState(false);
  const [mode, setMode] = useState<CompareMode>(defaultMode);

  const setPositionFromClientX = useCallback((clientX: number) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return;
    const pct = ((clientX - rect.left) / rect.width) * 100;
    setPosition(Math.min(100, Math.max(0, pct)));
  }, []);

  // Global listeners so a drag keeps tracking when the pointer leaves the sheet.
  useEffect(() => {
    if (!isDragging) return;
    const onMove = (e: MouseEvent) => setPositionFromClientX(e.clientX);
    const onTouchMove = (e: TouchEvent) => {
      if (e.touches[0]) setPositionFromClientX(e.touches[0].clientX);
    };
    const stop = () => setIsDragging(false);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", stop);
    window.addEventListener("touchmove", onTouchMove);
    window.addEventListener("touchend", stop);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", stop);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", stop);
    };
  }, [isDragging, setPositionFromClientX]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    const step = e.shiftKey ? 10 : 2;
    if (e.key === "ArrowLeft") { setPosition(p => Math.max(0, p - step)); e.preventDefault(); }
    if (e.key === "ArrowRight") { setPosition(p => Math.min(100, p + step)); e.preventDefault(); }
    if (e.key === "Home") { setPosition(0); e.preventDefault(); }
    if (e.key === "End") { setPosition(100); e.preventDefault(); }
  };

  // The overlay is needed while dragging, and always in hover mode, to receive the pointer
  // events the PDF iframes would otherwise capture.
  const overlayActive = isDragging || mode === "hover";
  // Snap instantly while the user is driving it; ease only for keyboard / reset jumps.
  const glide = isDragging || mode === "hover" ? "none" : "left 220ms cubic-bezier(.4,0,.2,1)";
  const paneSx = { position: "absolute" as const, inset: 0, border: "none", width: "100%", height: "100%" };

  return (
    <Box sx={{
      height, width: "100%", display: "flex", flexDirection: "column",
      borderRadius: "14px", overflow: "hidden", bgcolor: "#fff",
      border: "1px solid #e9ecf1", boxShadow: "0 2px 14px rgba(16,24,40,0.06)",
    }}>
      {/* Toolbar: which version is on which side, how to drive it, and a reset. */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}
        sx={{ px: 1.5, py: 1, borderBottom: "1px solid #eef1f5", bgcolor: "#fbfcfd", flexWrap: "wrap", rowGap: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Chip size="small" icon={<HistoryIcon />} label={beforeLabel}
            sx={{ bgcolor: "#eef1f5", color: "#475467", fontWeight: 600, "& .MuiChip-icon": { color: "#667085" } }} />
          <Typography variant="caption" color="text.secondary">vs</Typography>
          <Chip size="small" icon={<AutoAwesomeIcon />} label={afterLabel}
            sx={{ bgcolor: "#e7f4ed", color: ACCENT, fontWeight: 600, "& .MuiChip-icon": { color: ACCENT } }} />
        </Stack>

        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="caption" sx={{ color: "#667085", fontVariantNumeric: "tabular-nums" }}>
            {Math.round(position)}%
          </Typography>
          <ToggleButtonGroup size="small" exclusive value={mode}
            onChange={(_, v: CompareMode | null) => v && setMode(v)}
            sx={{
              "& .MuiToggleButton-root": { px: 1, py: 0.3, textTransform: "none", border: "1px solid #e4e7ec" },
              "& .Mui-selected": { bgcolor: "#e7f4ed !important", color: ACCENT + " !important" },
            }}>
            <ToggleButton value="drag">
              <Tooltip title="Drag the handle"><SwipeIcon fontSize="small" /></Tooltip>
            </ToggleButton>
            <ToggleButton value="hover">
              <Tooltip title="Follow the pointer"><HighlightAltIcon fontSize="small" /></Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>
          <Tooltip title="Reset to centre">
            <IconButton size="small" onClick={() => setPosition(50)}>
              <RestartAltIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      <Box ref={containerRef} sx={{
        position: "relative", flex: 1, overflow: "hidden",
        bgcolor: "#f4f6f8", userSelect: isDragging ? "none" : "auto",
      }}>
        {/* Older version fills the sheet. */}
        <Box component="iframe" title={beforeLabel} src={beforeUrl} sx={paneSx} />

        {/* Newer version, clipped to the right of the divider. */}
        <Box sx={{ position: "absolute", inset: 0, clipPath: `inset(0 0 0 ${position}%)` }}>
          <Box component="iframe" title={afterLabel} src={afterUrl} sx={paneSx} />
        </Box>

        {/* Corner tags, dimmed once their side is nearly wiped away. */}
        <Box sx={{
          position: "absolute", top: 12, left: 12, px: 1.25, py: 0.35, borderRadius: 5,
          bgcolor: "rgba(17,24,39,0.72)", color: "#fff", fontSize: 12, fontWeight: 600,
          pointerEvents: "none", opacity: position < 14 ? 0 : 1, transition: "opacity 160ms",
        }}>{beforeLabel}</Box>
        <Box sx={{
          position: "absolute", top: 12, right: 12, px: 1.25, py: 0.35, borderRadius: 5,
          bgcolor: "rgba(5,150,105,0.92)", color: "#fff", fontSize: 12, fontWeight: 600,
          pointerEvents: "none", opacity: position > 86 ? 0 : 1, transition: "opacity 160ms",
        }}>{afterLabel}</Box>

        {overlayActive && (
          <Box
            onMouseMove={mode === "hover" ? (e) => setPositionFromClientX(e.clientX) : undefined}
            onMouseDown={mode === "hover" ? undefined : () => setIsDragging(true)}
            sx={{ position: "absolute", inset: 0, cursor: "ew-resize", zIndex: 3 }}
          />
        )}

        {/* Divider: a thin line with a soft glow so it stays visible over dense invoice tables. */}
        <Box sx={{
          position: "absolute", top: 0, bottom: 0, left: `${position}%`, width: 2, zIndex: 4,
          bgcolor: ACCENT, transform: "translateX(-1px)", transition: glide, pointerEvents: "none",
          boxShadow: `0 0 0 1px rgba(255,255,255,0.9), 0 0 14px rgba(5,150,105,${isDragging ? 0.5 : 0.3})`,
        }} />

        <Box
          role="slider"
          aria-label="Compare invoice versions"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(position)}
          aria-valuetext={`${Math.round(position)} percent ${afterLabel}`}
          tabIndex={0}
          onKeyDown={onKeyDown}
          onMouseDown={() => setIsDragging(true)}
          onTouchStart={() => setIsDragging(true)}
          sx={{
            position: "absolute", top: "50%", left: `${position}%`, zIndex: 5,
            transform: `translate(-50%, -50%) scale(${isDragging ? 1.08 : 1})`,
            transition: `${glide}, transform 140ms ease`,
            width: 48, height: 48, borderRadius: "50%",
            bgcolor: "#fff", border: `2px solid ${ACCENT}`,
            display: "flex", alignItems: "center", justifyContent: "center",
            cursor: "ew-resize", touchAction: "none",
            boxShadow: "0 6px 18px rgba(5,150,105,0.3)",
            "&:hover": { boxShadow: "0 8px 22px rgba(5,150,105,0.38)" },
            "&:focus-visible": { outline: `3px solid rgba(5,150,105,0.4)`, outlineOffset: 3 },
          }}
        >
          <ChevronLeftIcon sx={{ color: ACCENT, fontSize: 20, ml: -0.6 }} />
          <ChevronRightIcon sx={{ color: ACCENT, fontSize: 20, mr: -0.6 }} />
        </Box>

        <Typography variant="caption" sx={{
          position: "absolute", bottom: 12, left: "50%", transform: "translateX(-50%)",
          bgcolor: "rgba(17,24,39,0.72)", color: "#fff", px: 1.5, py: 0.4, borderRadius: 5,
          pointerEvents: "none", opacity: isDragging ? 0 : 1, transition: "opacity 160ms",
        }}>
          {mode === "hover" ? "Move across to compare" : "Drag the handle, or use arrow keys"}
        </Typography>
      </Box>
    </Box>
  );
};

export default InvoicePdfComparison;
